/// <amd-module name="CodeCoverage/Timeline/Components/CodeCoverageTimelineInBuildView" />
import * as BuildContracts from 'Build/Client/Contracts/Build';
import { DetailsSection, DetailsSectionContent, DetailsSectionHeader, DetailsSectionSecondaryHeader } from 'Build/Shared/Components/DetailsSection';
import { IContributedTimelineSnapshotProps, ITimelineSnapshotProps } from 'Build/Shared/Components/Timeline';
import { IStatusIconProps, SnapshotCallBack } from 'Build/Shared/Components/Timeline/Timeline.types';
import { CodeCoverageActionCreator } from 'CodeCoverage/Common/Actions/CodeCoverageActionCreator';
import { CodeCoverageActionHub } from 'CodeCoverage/Common/Actions/CodeCoverageActionHub';
import { PerfScenarios } from 'CodeCoverage/Common/Constants';
import { RunViewArtifact } from 'CodeCoverage/Common/RunViewArtifact';
import { CodeCoverageTimelineSummary } from 'CodeCoverage/Timeline/Components/CodeCoverageTimelineSummary';
import * as Resources from 'CodeCoverage/Timeline/Resources';
import { CodeCoverageTimelineStore, ICodeCoverageTimelineForBuildState } from 'CodeCoverage/Timeline/Stores/CodeCoverageTimelineStore';
import * as React from 'react';
import { getTFSPageData } from 'Tfs/Platform/Page';
import * as Utils_String from 'VSS/Core/Util/String';
import { onClickFPS } from 'VSS/Platform/FPS';
import { Components, IVssComponentContext, VssComponent } from 'VSS/Platform/Layout';
import { routeUrl } from 'VSS/Platform/Location';
import { IVssPerformanceService } from 'VSS/Platform/Performance';
import { Link } from 'VSSUI/Link';
import { VssIconType } from 'VSSUI/VssIcon';


export class CodeCoverageTimeLineForBuildComponent extends VssComponent<IContributedTimelineSnapshotProps, ICodeCoverageTimelineForBuildState> {

	private _codeCoverageActionsHub: CodeCoverageActionHub;
	private _codeCoverageActionCreator: CodeCoverageActionCreator;
	private _codeCoverageTimelineStore: CodeCoverageTimelineStore;
	private _currentBuild: BuildContracts.Build;
	private _snapshotRenderCallback: SnapshotCallBack;
	private readonly CodeCoverageStoreChangeEvent = "codeCoverageStoreChangeEvent";
	private _updateCallback: () => void;
	private _perfService: IVssPerformanceService;
	private _codeCoverageTabUrl: string;

	constructor(props: IContributedTimelineSnapshotProps, context: IVssComponentContext) {
		super(props, context);
		this._perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
	}

	public componentWillMount() {
		this._codeCoverageActionsHub = new CodeCoverageActionHub();
		this._codeCoverageTimelineStore = new CodeCoverageTimelineStore(this.CodeCoverageStoreChangeEvent, this._codeCoverageActionsHub);
		this._codeCoverageActionCreator = new CodeCoverageActionCreator(this._codeCoverageActionsHub, this.context);
		this._codeCoverageTimelineStore.addChangedListener(this._onStoreStateChanged);
		this._perfService.startScenario(PerfScenarios.TimelineSummary, true);
	}

	public componentDidMount() {
		this._perfService.endScenario(PerfScenarios.Area, PerfScenarios.TimelineSummary, true);
	}

	public componentWillUnmount() {
		this._codeCoverageTimelineStore.removeChangedListener(this._onStoreStateChanged);
	}

	public render() {
		return (
			<SnapshotSampleHolder
				className="codecoverage-timeline-view"
				snapshotKey={this.props.snapshotKey}
				iconProps={
					{
						name: "TestBeaker",
						type: VssIconType.fabric
					}
				}
				statusIconProps={this._getStatusProps()}
				initializeSnapshot={this._initializeSnapshot}
				ref={this.props.snapshotRef}>

				<DetailsSection sectionKey="build-timeline" isExpandedByDefault={false} className="ci-hover-behavior">
					<DetailsSectionHeader renderAsHeading={true}>
						<Link className="header-text"
							onClick={this._onTimelineHeaderClick}
							href={this._getCodeCoverageTabUrl()}
							tooltipProps={{ text: Resources.CodeCoverageTimelineHeader, overflowOnly: true }}
						>
							{Resources.CodeCoverageTimelineHeader}
						</Link>
					</DetailsSectionHeader>
					<DetailsSectionSecondaryHeader>
						<span>{this._getSnapshotSubheader()}</span>
					</DetailsSectionSecondaryHeader>
					<DetailsSectionContent>
						<div className="codecoverage-summary-view">{this._getCodeCoverageTimelineSummaries()}</div>
					</DetailsSectionContent>
				</DetailsSection>
			</SnapshotSampleHolder>
		);
	}

	private _onTimelineHeaderClick(ev?: any) {
		if (this._codeCoverageTabUrl) {
			onClickFPS(this.context.pageContext, this._codeCoverageTabUrl, true, ev);
		}
	}

	private _getSnapshotSubheader(): string {
		if (!this.state || !this.state.shouldShowSummary) {
			return "";
		}

		const averageCoverage = (this.state.codeCoverageTimelineSummaryProps.reduce<number>((total, props) => total + props.coveredPercentage, 0) / this.state.codeCoverageTimelineSummaryProps.length).toFixed(2);
		return Utils_String.localeFormat(Resources.CodeCoverageTimelineSubHeader, averageCoverage, this.state.codeCoverageTimelineSummaryProps.length);
	}

	private _getCodeCoverageTimelineSummaries(): JSX.Element[] {
		if (!this.state || !this.state.shouldShowSummary) {
			return [];
		}

		return this.state.codeCoverageTimelineSummaryProps.map((props, i) => {
			return <CodeCoverageTimelineSummary {...props} key={"coverageTimelineSummary" + i} />
		});
	}

	private _getStatusProps = (): IStatusIconProps | undefined => {
		return {
			iconName: "CompletedSolid",
			className: "status-large-icon succeeded-status-color"
		} as IStatusIconProps;
	}

	private _initializeSnapshot = (build: BuildContracts.Build, callback: (marker: Date) => void, updateCallback: () => void) => {
		this._currentBuild = build;
		this._snapshotRenderCallback = callback;
		this._updateCallback = updateCallback;
		this._codeCoverageActionCreator.fetchCodeCoverageSummary(RunViewArtifact.GetArtifactDataFromBuild(build));
	}

	private _getCodeCoverageTabUrl(): string {
		if (this._codeCoverageTabUrl) {
			return this._codeCoverageTabUrl;
		}

		if (!this._currentBuild) {
			return "";
		}

		const newBuildHubRoute: string = "ms.vss-build-web.ci-results-hub-route";
		const testExtensionViewId = "codecoverage-tab";
		const tfsProjectData = getTFSPageData(this.context.pageContext);

		const routeValues: { [key: string]: string } = {};
		routeValues["buildId"] = this._currentBuild.id.toString();
		routeValues["view"] = testExtensionViewId;
		routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";

		this._codeCoverageTabUrl = routeUrl(this.context.pageContext, newBuildHubRoute, routeValues);
		return this._codeCoverageTabUrl;
	}

	private _onStoreStateChanged = () => {
		const currentState = this._codeCoverageTimelineStore.getState();

		if ((!this.state || !this.state.hasOwnProperty("shouldShowSummary")) && currentState.shouldShowSummary) {
			this.setState(currentState);
			this._perfService.addSplitTiming("ShowTimelineSummary", PerfScenarios.TimelineSummary);

			// Currently we have no way of getting at what time the code coverage completed.
			// Since the spec is to show the code coverage timeline snapshot above the test
			// timeline snapshot, we add one second to the time used by the test timlene snapshot
			const time = this._currentBuild.startTime || this._currentBuild.queueTime;
			time.setSeconds(time.getSeconds() + 1);

			this._snapshotRenderCallback(time);
			this._updateCallback();
		}
	}
}

// By doing this, we can avoid dependency on the Timeline component code it'self
// Otherwise, if it's already pulled in, then we could use TimelineSnapshot component
export class SnapshotSampleHolder extends React.Component<ITimelineSnapshotProps<BuildContracts.Build>, {}> {
	public render() {
		return <div>Note! This is just a holder, this text would never show up!</div>;
	}
}

Components.add("codeCoverageTimeLineForBuildComponent", CodeCoverageTimeLineForBuildComponent);
